import { Injectable, inject } from '@angular/core';
import {
  Auth,
  GoogleAuthProvider,
  User,
  authState,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { get } from 'lodash-es';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth: Auth = inject(Auth);
  private router = inject(Router);

  public user$: Observable<User | null> = authState(this.auth);

  /**
   * Sign in with email and password
   */
  async signInWithEmail(email: string, password: string): Promise<void> {
    try {
      await signInWithEmailAndPassword(this.auth, email, password);
    } catch (error: unknown) {
      throw this.handleError(error as { code: string; message?: string });
    }
  }

  /**
   * Sign up with email and password
   */
  async signUpWithEmail(email: string, password: string): Promise<void> {
    try {
      await createUserWithEmailAndPassword(this.auth, email, password);
    } catch (error: unknown) {
      throw this.handleError(error as { code: string; message?: string });
    }
  }

  /**
   * Sign in with Google
   */
  async signInWithGoogle(): Promise<void> {
    try {
      const provider = new GoogleAuthProvider();
      // Firebase automatically handles account linking when "One account per email address"
      // is enabled in the Firebase Console under Authentication -> Settings
      await signInWithPopup(this.auth, provider);
    } catch (error: unknown) {
      throw this.handleError(error as { code: string; message?: string });
    }
  }

  /**
   * Send password reset email
   */
  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error: unknown) {
      throw this.handleError(error as { code: string; message?: string });
    }
  }

  /**
   * Sign out
   */
  async signOut(): Promise<void> {
    try {
      await this.auth.signOut();
      this.router.navigate(['/home']);
    } catch (error: unknown) {
      throw this.handleError(error as { code: string; message?: string });
    }
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.auth.currentUser;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.auth.currentUser !== null;
  }

  private codeToMessageMap: { [code: string]: string } = {
    'auth/email-already-in-use': $localize `This email is already registered`,
    'auth/invalid-email': $localize `Invalid email address`,
    'auth/operation-not-allowed': $localize `This operation is not allowed`,
    'auth/weak-password': $localize `Password is too weak`,
    'auth/user-disabled': $localize `This account has been disabled`,
    'auth/user-not-found': $localize `No account found with this email`,
    'auth/wrong-password': $localize `Incorrect password`,
    'auth/too-many-requests': $localize `Too many failed attempts. Please try again later`,
    'auth/popup-closed-by-user': $localize `Sign-in popup was closed`,
  };

  /**
   * Handle Firebase auth errors
   */
  private handleError(error: { code: string, message?: string}): Error {
    const message = get(this.codeToMessageMap, error.code, error.message || $localize `An error occurred during authentication`);
    return new Error(message);
  }
}
